import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { getEndpoints } from '~core/constants/endpoints.constants';
import { ThemeManagerService } from '~core/services/ui/theme-manager.service';
import { AuthService } from '~core/services/auth.service';
import { UserProfile, CompanyProfile } from '~core/models/profile.model';
import { ApiResponse } from '~shared/types/api-response.types';

@Injectable({
    providedIn: 'root'
})
export class ProfileService {
    private endpoints = getEndpoints();
    private authService = inject(AuthService);
    private themeManager = inject(ThemeManagerService);

    private lastFetchTimestamp = 0;
    private readonly CACHE_TTL_MS = 5 * 60 * 1000;

    private _userProfile = signal<UserProfile | null>(null);
    readonly userProfile = this._userProfile.asReadonly();

    readonly currentCompany = computed<CompanyProfile | null>(() => this.userProfile()?.defaultcompany ?? null);
    // token utente ricevuto da /profile: le chiamate autenticate devono attenderne la disponibilità
    readonly userToken = computed<string | null>(() => this.userProfile()?.token ?? null);
    readonly requiresDestinationCountry = computed<boolean>(() => this.userProfile()?.prezzi_ivati === true);

    readonly profileResource = httpResource<ApiResponse<UserProfile> | undefined>(() => {
        const email = this.authService.authEmail();
        if (!email) return undefined;
        this.lastFetchTimestamp = Date.now();
        return {
            url: this.endpoints.auth.v3.profile,
            method: 'POST',
            body: { email }
        };
    });

    constructor() {
        effect(() => {
            if (this.profileResource.hasValue()) {
                const res = this.profileResource.value();
                const profile = res && 'data' in res ? res.data : null;
                this._userProfile.set(profile);

                if (profile?.defaultcompany) {
                    this.themeManager.setColor(
                        profile.defaultcompany.primaryColor ?? '#b13e90',
                        profile.defaultcompany.hoverColor ?? '#8e3173'
                    );
                }
            }
        });
    }

    reloadProfile(force = false): void {
        const isExpired = Date.now() - this.lastFetchTimestamp > this.CACHE_TTL_MS;
        const hasData = this.profileResource.hasValue();

        if (force || !hasData || isExpired) {
            this.lastFetchTimestamp = Date.now();
            this.profileResource.reload();
        }
    }

    clearProfile(): void {
        this._userProfile.set(null);
    }
}
